import { useState, useEffect, useCallback, useRef } from 'react';
import { isTablet } from 'react-device-detect';


/**
 * Comprehensive device detection hook for fraud prevention
 * Detects DevTools mobile emulation across Chrome, Firefox, Safari, and Edge
 *
 * @returns {Object} Detection results
 * @property {boolean} isTrulyMobile - Device is genuinely mobile (not emulated)
 * @property {boolean} isEmulated - Desktop browser emulating mobile
 * @property {boolean} isTabletDevice - Device is a tablet
 * @property {number} emulationScore - 0-100+ confidence score (>=70 = likely emulated)
 * @property {string[]} flags - Array of triggered detection signals
 * @property {Object} signals - Raw signal data for logging
 * @property {boolean} isLoading - Still collecting async signals
 */

// Desktop GPU patterns - these cannot be faked by DevTools
const DESKTOP_GPU_PATTERNS = [
  // NVIDIA
  /nvidia/i,
  /geforce/i,
  /quadro/i,
  /rtx/i,
  /gtx/i,
  // AMD
  /radeon/i,
  /amd/i,
  /rx\s?\d{3,4}/i,
  // Intel Desktop/Laptop
  /intel.*(?:hd|uhd|iris|arc)/i,
  // Generic desktop indicators
  /mesa/i,
  /llvmpipe/i,
  // Virtual machines (also suspicious)
  /vmware/i,
  /virtualbox/i,
  /parallels/i,
];

// Mobile GPU patterns - legitimate mobile devices
const MOBILE_GPU_PATTERNS = [
  /apple gpu/i,
  /apple m\d/i,
  /adreno/i,
  /mali/i,
  /powervr/i,
  /img clover/i,
  /samsung xclipse/i,
];

// Desktop platform values that DevTools cannot change
const DESKTOP_PLATFORMS = [
  'macintel',
  'macppc',
  'mac68k',
  'win32',
  'win64',
  'windows',
  'linux x86_64',
  'linux i686',
  'freebsd',
  'openbsd',
];

// Known device screen specifications for consistency checks
const DEVICE_SPECS = {
  'iphone': { minDPR: 2, maxDPR: 3, maxWidth: 430 },
  'ipad': { minDPR: 2, maxDPR: 2, maxWidth: 1024 },
  'pixel': { minDPR: 2.5, maxDPR: 3.5, maxWidth: 412 },
  'samsung': { minDPR: 2, maxDPR: 4, maxWidth: 480 },
  'galaxy': { minDPR: 2, maxDPR: 4, maxWidth: 480 },
};

/**
 * Get WebGL renderer info - reveals actual GPU
 */
const getGPUInfo = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) {
      return { vendor: null, renderer: null, error: 'webgl_unavailable' };
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

    if (!debugInfo) {
      // Some browsers hide this - not necessarily suspicious
      return { vendor: null, renderer: null, error: 'debug_info_unavailable' };
    }

    return {
      vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
      renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
      error: null,
    };
  } catch (e) {
    return { vendor: null, renderer: null, error: e.message };
  }
};

/**
 * Analyze GPU string to determine if it's desktop or mobile
 */
const analyzeGPU = (renderer) => {
  if (!renderer) return { isDesktop: false, isMobile: false, confidence: 0 };

  const isDesktop = DESKTOP_GPU_PATTERNS.some(pattern => pattern.test(renderer));
  const isMobile = MOBILE_GPU_PATTERNS.some(pattern => pattern.test(renderer));

  return {
    isDesktop,
    isMobile,
    confidence: isDesktop ? 0.95 : (isMobile ? 0.9 : 0.5),
    raw: renderer,
  };
};

/**
 * Check if user agent claims to be mobile
 */
const userAgentClaimsMobile = () => {
  const ua = navigator.userAgent.toLowerCase();
  return /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini|webos/i.test(ua);
};

/**
 * Extract claimed device from user agent
 */
const getClaimedDevice = () => {
  const ua = navigator.userAgent;

  if (/iPhone/i.test(ua)) return 'iphone';
  if (/iPad/i.test(ua)) return 'ipad';
  if (/Pixel/i.test(ua)) return 'pixel';
  if (/Samsung|SM-/i.test(ua)) return 'samsung';
  if (/Galaxy/i.test(ua)) return 'galaxy';
  if (/Android/i.test(ua)) return 'android';

  return null;
};

/**
 * Detect navigator property tampering via instance-level getter injection
 * Userscripts (Tampermonkey, etc.) override navigator properties by defining
 * instance-level getters that shadow the prototype getters.
 */
const detectPropertyTampering = () => {
  const flags = [];

  try {
    // Check if navigator.platform has an instance-level getter (should only exist on prototype)
    const platformDesc = Object.getOwnPropertyDescriptor(navigator, 'platform');
    if (platformDesc && typeof platformDesc.get === 'function') {
      flags.push('PLATFORM_INSTANCE_GETTER');
    }

    // Check if navigator.userAgentData has an instance-level getter
    const uaDataDesc = Object.getOwnPropertyDescriptor(navigator, 'userAgentData');
    if (uaDataDesc && typeof uaDataDesc.get === 'function') {
      flags.push('UA_DATA_INSTANCE_GETTER');
    }

    // Check if Navigator.prototype.platform getter has been replaced
    const protoDesc = Object.getOwnPropertyDescriptor(Navigator.prototype, 'platform');
    if (protoDesc && typeof protoDesc.get === 'function') {
      const getterStr = protoDesc.get.toString();
      if (!getterStr.includes('[native code]')) {
        flags.push('PLATFORM_PROTOTYPE_MODIFIED');
      }
    }

    // Check if navigator.vendor has an instance-level getter
    const vendorDesc = Object.getOwnPropertyDescriptor(navigator, 'vendor');
    if (vendorDesc && typeof vendorDesc.get === 'function') {
      flags.push('VENDOR_INSTANCE_GETTER');
    }
  } catch (e) {
    // Property descriptor access failed — not necessarily tampering
  }

  return { isTampered: flags.length > 0, flags };
};

/**
 * Detect function prototype tampering
 * Checks if key browser API functions have been monkey-patched by
 * verifying their toString() still contains [native code].
 */
const detectFunctionTampering = () => {
  const flags = [];

  const checks = [
    {
      fn: () => WebGLRenderingContext?.prototype?.getParameter,
      flag: 'WEBGL_GET_PARAMETER_MODIFIED',
    },
    {
      fn: () => WebGL2RenderingContext?.prototype?.getParameter,
      flag: 'WEBGL2_GET_PARAMETER_MODIFIED',
    },
    {
      fn: () => HTMLCanvasElement?.prototype?.getContext,
      flag: 'CANVAS_GET_CONTEXT_MODIFIED',
    },
    {
      fn: () => navigator?.mediaDevices?.enumerateDevices,
      flag: 'MEDIA_DEVICES_MODIFIED',
    },
  ];

  for (const { fn, flag } of checks) {
    try {
      const func = fn();
      if (func && typeof func === 'function') {
        const str = Function.prototype.toString.call(func);
        if (!str.includes('[native code]')) {
          flags.push(flag);
        }
      }
    } catch (e) {
      // Access failed — skip this check
    }
  }

  return { isTampered: flags.length > 0, flags };
};

/**
 * Detect spoofing via iframe environment mismatch
 * Creates a clean sandboxed iframe and compares navigator properties
 * between the main window and the pristine iframe context.
 * Userscripts typically only tamper with the main window.
 */
const detectViaMismatch = () => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      cleanup();
      resolve({ isTampered: false, flags: [], mainWindow: {}, cleanIframe: {} });
    }, 2000);

    let iframe;

    const cleanup = () => {
      clearTimeout(timeout);
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    try {
      iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
      iframe.sandbox = 'allow-same-origin';

      iframe.onload = () => {
        try {
          const flags = [];
          const iframeWindow = iframe.contentWindow;
          const iframeNav = iframeWindow.navigator;

          const mainWindow = {
            platform: navigator.platform,
            mobile: navigator.userAgentData?.mobile,
            uaPlatform: navigator.userAgentData?.platform,
            vendor: navigator.vendor,
            maxTouchPoints: navigator.maxTouchPoints,
          };

          const cleanIframe = {
            platform: iframeNav.platform,
            mobile: iframeNav.userAgentData?.mobile,
            uaPlatform: iframeNav.userAgentData?.platform,
            vendor: iframeNav.vendor,
            maxTouchPoints: iframeNav.maxTouchPoints,
          };

          if (mainWindow.platform !== cleanIframe.platform) {
            flags.push('PLATFORM_MISMATCH');
          }
          if (mainWindow.mobile !== undefined && cleanIframe.mobile !== undefined &&
              mainWindow.mobile !== cleanIframe.mobile) {
            flags.push('MOBILE_FLAG_MISMATCH');
          }
          if (mainWindow.uaPlatform !== undefined && cleanIframe.uaPlatform !== undefined &&
              mainWindow.uaPlatform !== cleanIframe.uaPlatform) {
            flags.push('UA_PLATFORM_MISMATCH');
          }
          if (mainWindow.vendor !== cleanIframe.vendor) {
            flags.push('VENDOR_MISMATCH');
          }
          if (mainWindow.maxTouchPoints !== cleanIframe.maxTouchPoints) {
            flags.push('TOUCH_POINTS_MISMATCH');
          }

          cleanup();
          resolve({ isTampered: flags.length > 0, flags, mainWindow, cleanIframe });
        } catch (e) {
          cleanup();
          resolve({ isTampered: false, flags: [], mainWindow: {}, cleanIframe: {} });
        }
      };

      iframe.onerror = () => {
        cleanup();
        resolve({ isTampered: false, flags: [], mainWindow: {}, cleanIframe: {} });
      };

      document.body.appendChild(iframe);
    } catch (e) {
      cleanup();
      resolve({ isTampered: false, flags: [], mainWindow: {}, cleanIframe: {} });
    }
  });
};

/**
 * Collect all static signals (synchronous)
 */
const collectStaticSignals = () => {
  const claimsMobile = userAgentClaimsMobile();
  const claimedDevice = getClaimedDevice();
  const gpu = getGPUInfo();
  const gpuAnalysis = analyzeGPU(gpu.renderer);

  // Platform check - DevTools cannot fake this
  const platform = (navigator.platform || '').toLowerCase();
  const isDesktopPlatform = DESKTOP_PLATFORMS.some(p => platform.includes(p));

  // Chrome 90+ User Agent Client Hints API
  const uaData = navigator.userAgentData;
  const uaDataMobile = uaData?.mobile;
  const uaDataPlatform = uaData?.platform?.toLowerCase();
  // NOTE: On foldable phones (Galaxy Z Fold, Pixel Fold, etc.) with large inner
  // screens, Chrome reports userAgentData.mobile = false because the unfolded
  // display exceeds Chrome's width threshold. We must NOT treat this as a desktop
  // signal when the platform is "Android" — the GPU and platform checks will
  // catch real desktop emulation regardless.
  const isAndroidPlatform = uaDataPlatform === 'android';
  const isUADataDesktop = uaData && (
    (uaDataMobile === false && !isAndroidPlatform) ||
    ['macos', 'windows', 'linux', 'chrome os'].includes(uaDataPlatform)
  );

  // Pointer and hover capabilities
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
  const hasHoverCapability = window.matchMedia('(hover: hover)').matches;
  const hasNoHover = window.matchMedia('(hover: none)').matches;

  // Touch capabilities
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const hasTouchEvents = 'ontouchstart' in window;

  // Screen characteristics
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const devicePixelRatio = window.devicePixelRatio || 1;
  const colorDepth = window.screen.colorDepth;

  // Screen orientation
  const orientation = window.screen.orientation?.type || 'unknown';

  // Hardware specs
  const deviceMemory = navigator.deviceMemory; // Chrome only
  const hardwareConcurrency = navigator.hardwareConcurrency;

  // Connection info
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const connectionType = connection?.type;
  const connectionEffectiveType = connection?.effectiveType;

  // Screen consistency check
  let screenConsistent = true;
  if (claimedDevice && DEVICE_SPECS[claimedDevice]) {
    const specs = DEVICE_SPECS[claimedDevice];
    const minDim = Math.min(screenWidth, screenHeight);
    screenConsistent = (
      devicePixelRatio >= specs.minDPR &&
      devicePixelRatio <= specs.maxDPR &&
      minDim <= specs.maxWidth * 1.1 // 10% tolerance
    );
  }

  // Spoof detection (synchronous checks)
  const propertyTampering = detectPropertyTampering();
  const functionTampering = detectFunctionTampering();

  return {
    // Claim analysis
    claimsMobile,
    claimedDevice,

    // Platform signals (high confidence)
    platform,
    isDesktopPlatform,

    // User Agent Data API (Chrome/Edge)
    uaDataMobile,
    uaDataPlatform,
    isUADataDesktop,
    isAndroidPlatform,

    // GPU signals (highest confidence)
    gpuVendor: gpu.vendor,
    gpuRenderer: gpu.renderer,
    gpuError: gpu.error,
    gpuIsDesktop: gpuAnalysis.isDesktop,
    gpuIsMobile: gpuAnalysis.isMobile,
    gpuConfidence: gpuAnalysis.confidence,

    // Pointer/hover signals
    hasCoarsePointer,
    hasFinePointer,
    hasHoverCapability,
    hasNoHover,

    // Touch signals
    maxTouchPoints,
    hasTouchEvents,

    // Screen signals
    screenWidth,
    screenHeight,
    devicePixelRatio,
    colorDepth,
    orientation,
    screenConsistent,

    // Hardware signals
    deviceMemory,
    hardwareConcurrency,

    // Connection signals
    connectionType,
    connectionEffectiveType,

    // Spoof detection signals
    spoofFlags: [...propertyTampering.flags, ...functionTampering.flags],
    isSpoofDetected: propertyTampering.isTampered || functionTampering.isTampered,
  };
};

/**
 * Calculate emulation score from signals
 *
 * IMPORTANT: Only use signals that reliably differentiate desktop emulation from real mobile.
 * Sensor checks (motion/orientation) are UNRELIABLE because:
 * - Modern browsers require permission/gesture to access sensors
 * - WebViews often don't have sensor access
 * - Battery saver modes disable sensors
 * - The timeout may be too short for sensors to fire
 */
const calculateEmulationScore = (signals, sensorData, touchData) => {
  let score = 0;
  const flags = [];

  // Only run detection if UA claims mobile
  if (!signals.claimsMobile) {
    return { score: 0, flags: ['not_claiming_mobile'], isEmulated: false };
  }

  // ========== TIER 0: SPOOF DETECTION (Userscript / extension tampering) ==========

  // Synchronous spoof flags from property and function tampering checks
  if (signals.spoofFlags && signals.spoofFlags.length > 0) {
    const spoofScore = Math.min(signals.spoofFlags.length * 100, 200);
    score += spoofScore;
    flags.push(...signals.spoofFlags);
  }

  // Async iframe mismatch flags (merged from spoofData if available)
  if (sensorData.iframeSpoofFlags && sensorData.iframeSpoofFlags.length > 0) {
    const iframeScore = Math.min(sensorData.iframeSpoofFlags.length * 100, 200);
    score += iframeScore;
    flags.push(...sensorData.iframeSpoofFlags);
  }

  // ========== TIER 1: DEFINITIVE DETECTION (Cannot be faked by DevTools) ==========

  // GPU mismatch - DevTools cannot fake WebGL renderer
  if (signals.gpuIsDesktop) {
    score += 100;
    flags.push('desktop_gpu_detected');
  }

  // Platform mismatch - DevTools cannot fake navigator.platform
  if (signals.isDesktopPlatform) {
    score += 100;
    flags.push('desktop_platform');
  }

  // User Agent Data API mismatch (Chrome/Edge 90+)
  // This is the most reliable check for Chrome-based browsers
  if (signals.isUADataDesktop) {
    score += 100;
    flags.push('ua_data_desktop');
  }

  // ========== TIER 1.5: FOLDABLE / ANDROID LARGE-SCREEN AWARENESS ==========

  // On foldable phones (Galaxy Z Fold, Pixel Fold), Chrome reports
  // userAgentData.mobile = false when the large inner screen is active.
  // This is NOT emulation — but we need compensating controls:
  //
  // 1. If claiming Android + mobile=false but GPU is NOT a recognized mobile GPU,
  //    that's highly suspicious (emulator, cloud device, or spoofed platform).
  //    Real foldables always have Adreno/Mali/etc.
  //
  // 2. If claiming Android + mobile=false with a valid mobile GPU,
  //    add a soft signal only (legitimate foldable).
  if (signals.uaDataMobile === false && signals.uaDataPlatform === 'android') {
    if (!signals.gpuIsMobile) {
      // Android claims + large screen + no recognized mobile GPU = suspicious
      // Catches: Android emulators (SwiftShader/mesa), CDP spoofing platform
      score += 80;
      flags.push('android_large_screen_no_mobile_gpu');
    } else {
      // Legitimate foldable phone (Z Fold, Pixel Fold) — soft signal only
      score += 20;
      flags.push('foldable_large_screen');
    }
  }

  // ========== TIER 2: STRONG INDICATORS ==========

  // Fine pointer only (mouse) without coarse pointer (touch)
  // Real mobile devices have coarse pointer, desktops have fine pointer
  if (signals.hasFinePointer && !signals.hasCoarsePointer) {
    score += 50;
    flags.push('fine_pointer_only');
  }

  // Hover capability without no-hover
  // Real touchscreens report hover:none, desktops report hover:hover
  if (signals.hasHoverCapability && !signals.hasNoHover) {
    score += 40;
    flags.push('has_hover_capability');
  }

  // Zero touch points while claiming mobile
  if (signals.maxTouchPoints === 0) {
    score += 40;
    flags.push('no_touch_points');
  }

  // ========== TIER 3: SUPPORTING EVIDENCE (Lower weight) ==========

  // High device memory (> 8GB very unusual for mobile)
  if (signals.deviceMemory && signals.deviceMemory > 8) {
    score += 15;
    flags.push('high_device_memory');
  }

  // Very high CPU cores (> 12 unusual for mobile)
  if (signals.hardwareConcurrency && signals.hardwareConcurrency > 12) {
    score += 10;
    flags.push('high_cpu_cores');
  }

  // ========== REMOVED/DISABLED CHECKS ==========
  // The following checks have been disabled due to high false positive rates:
  //
  // - Motion sensors: Many real mobile browsers require permission or don't fire in WebViews
  // - Orientation sensors: Same issues as motion sensors
  // - Screen mismatch: Budget phones have different specs than flagships
  // - Touch radius/force: Not all browsers report these values
  // - Battery state: Varies too much across devices

  // Log removed checks for debugging (but don't add to score)
  if (sensorData.motionChecked && !sensorData.hasMotion) {
    flags.push('info_no_motion_sensors');
  }
  if (sensorData.orientationChecked && !sensorData.hasOrientation) {
    flags.push('info_no_orientation_sensors');
  }

  return {
    score,
    flags,
    isEmulated: score >= 70,
    confidence: Math.min(score / 100, 1),
  };
};

/**
 * Main hook
 */
const useDeviceDetection = (options = {}) => {
  const {
    sensorTimeout = 1000,      // How long to wait for sensor data
    enableTouchValidation = true,  // Validate touch events
    onEmulationDetected = null,    // Callback when emulation detected
    requestId = null,              // Request ID for tracking
    serverReportUrl = null,        // URL to POST raw signals for server-side cross-referencing
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [signals, setSignals] = useState(null);
  const [sensorData, setSensorData] = useState({
    motionChecked: false,
    hasMotion: false,
    motionIsStatic: false,
    motionSamples: [],
    orientationChecked: false,
    hasOrientation: false,
    batteryLevel: null,
    batteryCharging: null,
    iframeSpoofFlags: [],
  });
  const [spoofData, setSpoofData] = useState({
    isTampered: false,
    flags: [],
    mainWindow: {},
    cleanIframe: {},
  });
  const [touchData, setTouchData] = useState({
    checked: false,
    radiusX: null,
    radiusY: null,
    force: null,
  });
  const [result, setResult] = useState({
    isTrulyMobile: false,
    isEmulated: false,
    isTabletDevice: isTablet,
    emulationScore: 0,
    flags: [],
    confidence: 0,
  });

  const [serverTamperDetected, setServerTamperDetected] = useState(false);
  const [serverCheckPending, setServerCheckPending] = useState(!!serverReportUrl);

  const motionSamplesRef = useRef([]);
  const sensorTimeoutRef = useRef(null);
  const onEmulationDetectedRef = useRef(onEmulationDetected);

  const signalReportSentRef = useRef(false);

  // Keep callback ref updated
  useEffect(() => {
    onEmulationDetectedRef.current = onEmulationDetected;
  }, [onEmulationDetected]);

  // Collect static signals on mount
  useEffect(() => {
    const staticSignals = collectStaticSignals();
    setSignals(staticSignals);

    // Check for tablet using both react-device-detect and screen size
    const isTabletDevice = isTablet ||
      (staticSignals.claimsMobile && Math.min(staticSignals.screenWidth, staticSignals.screenHeight) > 600);

    setResult(prev => ({ ...prev, isTabletDevice }));
  }, []);

  // Collect sensor data (async)
  useEffect(() => {
    if (!signals?.claimsMobile) {
      setIsLoading(false);
      return;
    }

    let motionHandler, orientationHandler;
    let motionReceived = false;
    let orientationReceived = false;

    // Motion sensor check
    motionHandler = (event) => {
      motionReceived = true;
      const { accelerationIncludingGravity: accel } = event;

      if (accel) {
        motionSamplesRef.current.push({
          x: accel.x,
          y: accel.y,
          z: accel.z,
          timestamp: Date.now(),
        });
      }

      setSensorData(prev => ({
        ...prev,
        hasMotion: accel && (accel.x !== null || accel.y !== null || accel.z !== null),
        motionSamples: motionSamplesRef.current,
      }));
    };

    // Orientation sensor check
    orientationHandler = (event) => {
      orientationReceived = true;
      const hasData = event.alpha !== null || event.beta !== null || event.gamma !== null;

      setSensorData(prev => ({
        ...prev,
        hasOrientation: hasData,
      }));
    };

    window.addEventListener('devicemotion', motionHandler);
    window.addEventListener('deviceorientation', orientationHandler);

    // Battery check
    if (navigator.getBattery) {
      navigator.getBattery().then(battery => {
        setSensorData(prev => ({
          ...prev,
          batteryLevel: battery.level,
          batteryCharging: battery.charging,
        }));
      }).catch(() => {});
    }

    // Iframe mismatch detection (async)
    detectViaMismatch().then(mismatchResult => {
      setSpoofData(mismatchResult);
      if (mismatchResult.flags.length > 0) {
        setSensorData(prev => ({
          ...prev,
          iframeSpoofFlags: mismatchResult.flags,
        }));
      }
    });

    // Timeout - mark sensors as checked after waiting
    sensorTimeoutRef.current = setTimeout(() => {
      const samples = motionSamplesRef.current;
      let motionIsStatic = false;

      // Check if motion data is suspiciously static
      if (samples.length >= 3) {
        const variance = samples.reduce((acc, s, i, arr) => {
          if (i === 0) return 0;
          const prev = arr[i - 1];
          return acc + Math.abs(s.x - prev.x) + Math.abs(s.y - prev.y) + Math.abs(s.z - prev.z);
        }, 0);
        motionIsStatic = variance < 0.01; // Suspiciously stable
      }

      setSensorData(prev => ({
        ...prev,
        motionChecked: true,
        orientationChecked: true,
        motionIsStatic,
        hasMotion: motionReceived && prev.hasMotion,
        hasOrientation: orientationReceived && prev.hasOrientation,
      }));

      setIsLoading(false);
    }, sensorTimeout);

    return () => {
      window.removeEventListener('devicemotion', motionHandler);
      window.removeEventListener('deviceorientation', orientationHandler);
      if (sensorTimeoutRef.current) {
        clearTimeout(sensorTimeoutRef.current);
      }
    };
  }, [signals?.claimsMobile, sensorTimeout]);

  // Touch validation handler
  const validateTouch = useCallback((event) => {
    if (!enableTouchValidation || touchData.checked) return;

    const touch = event.touches?.[0] || event.changedTouches?.[0];
    if (!touch) return;

    setTouchData({
      checked: true,
      radiusX: touch.radiusX ?? 0,
      radiusY: touch.radiusY ?? 0,
      force: touch.force ?? touch.webkitForce ?? 0,
      rotationAngle: touch.rotationAngle ?? 0,
    });
  }, [enableTouchValidation, touchData.checked]);

  // Attach touch listener
  useEffect(() => {
    if (!enableTouchValidation || !signals?.claimsMobile) return;

    document.addEventListener('touchstart', validateTouch, { passive: true });

    return () => {
      document.removeEventListener('touchstart', validateTouch);
    };
  }, [enableTouchValidation, signals?.claimsMobile, validateTouch]);

  // Calculate final result when data changes
  useEffect(() => {
    if (!signals) return;

    const { score, flags, isEmulated, confidence } = calculateEmulationScore(
      signals,
      sensorData,
      touchData
    );

    const isTrulyMobile = signals.claimsMobile && !isEmulated;

    const newResult = {
      isTrulyMobile,
      isEmulated,
      isTabletDevice: result.isTabletDevice,
      emulationScore: score,
      flags,
      confidence,
    };

    setResult(newResult);

    // Callback when emulation detected
    if (isEmulated && onEmulationDetectedRef.current) {
      onEmulationDetectedRef.current({
        score,
        flags,
        signals,
        sensorData,
        touchData,
        spoofData,
      });
    }
  }, [signals, sensorData, touchData, spoofData, result.isTabletDevice]);

  // Send raw signals to server for cross-referencing against HTTP headers
  useEffect(() => {
    if (isLoading || !signals || !serverReportUrl || signalReportSentRef.current) return;
    signalReportSentRef.current = true;

    const payload = {
      request_id: requestId,
      timestamp: new Date().toISOString(),
      signals: {
        platform: signals.platform,
        user_agent: navigator.userAgent,
        ua_data_mobile: signals.uaDataMobile,
        ua_data_platform: signals.uaDataPlatform,
        max_touch_points: signals.maxTouchPoints,
        gpu_renderer: signals.gpuRenderer,
        gpu_vendor: signals.gpuVendor,
        device_memory: signals.deviceMemory,
        hardware_concurrency: signals.hardwareConcurrency,
        screen_width: signals.screenWidth,
        screen_height: signals.screenHeight,
        device_pixel_ratio: signals.devicePixelRatio,
        has_coarse_pointer: signals.hasCoarsePointer,
        has_fine_pointer: signals.hasFinePointer,
        has_hover: signals.hasHoverCapability,
        color_depth: signals.colorDepth,
      },
      client_assessment: {
        emulation_score: result.emulationScore,
        is_emulated: result.isEmulated,
        flags: result.flags,
        spoof_detected: signals.isSpoofDetected || spoofData.isTampered,
        spoof_flags: [
          ...(signals.spoofFlags || []),
          ...(spoofData.flags || []),
        ],
      },
      iframe_comparison: {
        main_window: spoofData.mainWindow,
        clean_iframe: spoofData.cleanIframe,
      },
    };

    fetch(serverReportUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data.blocked) {
          console.log('[DEVICE DETECTION] Server-side tamper detected — session blocked');
          setServerTamperDetected(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        setServerCheckPending(false);
      });
  }, [isLoading, signals, serverReportUrl, requestId, result, spoofData]);

  return {
    ...result,
    serverTamperDetected,
    serverCheckPending,
    signals,
    sensorData,
    touchData,
    spoofData,
    isLoading,
    validateTouch, // Expose for manual touch validation
  };
};

export default useDeviceDetection;

// Named exports for specific checks
export {
  getGPUInfo,
  analyzeGPU,
  userAgentClaimsMobile,
  collectStaticSignals,
  calculateEmulationScore,
  detectPropertyTampering,
  detectFunctionTampering,
  detectViaMismatch,
  DESKTOP_GPU_PATTERNS,
  MOBILE_GPU_PATTERNS,
  DESKTOP_PLATFORMS,
};
